import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  CreditCard, Loader2, RefreshCw, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import adminService from '../../services/adminService';

const PAYMENT_STATUSES = ['Paid', 'Pending', 'Failed', 'Refunded'];

function PaginatedTable({ columns, rows, data, page, setPage, isLoading }) {
  return (
    <>
      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map(c => <TableHead key={c}>{c}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow key={i}>
                    {row.map((cell, j) => (
                      <TableCell key={j}>{cell}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Page {data.page} of {data.totalPages} ({data.total} records)
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= data.totalPages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

const statusBadge = (status) => {
  const s = status?.toLowerCase();
  if (s === 'paid') return <Badge className="bg-green-100 text-green-800">Paid</Badge>;
  if (s === 'pending') return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
  if (s === 'failed') return <Badge className="bg-red-100 text-red-800">Failed</Badge>;
  return <Badge variant="secondary">{status}</Badge>;
};

export default function AdminPaymentsPage() {
  const [activeTab, setActiveTab] = useState('history');

  // Payment History state
  const [histData, setHistData] = useState({ items: [], total: 0, page: 1, pageSize: 25, totalPages: 0 });
  const [histLoading, setHistLoading] = useState(true);
  const [histPage, setHistPage] = useState(1);
  const [histStatus, setHistStatus] = useState('');
  const [histDateFrom, setHistDateFrom] = useState('');
  const [histDateTo, setHistDateTo] = useState('');

  // Subscriptions state
  const [subData, setSubData] = useState({ items: [], total: 0, page: 1, pageSize: 25, totalPages: 0 });
  const [subLoading, setSubLoading] = useState(true);
  const [subPage, setSubPage] = useState(1);
  const [subStatus, setSubStatus] = useState('');

  const fetchHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const filter = { page: histPage, pageSize: 25 };
      if (histStatus) filter.status = histStatus;
      if (histDateFrom) filter.dateFrom = histDateFrom;
      if (histDateTo) filter.dateTo = histDateTo;
      const result = await adminService.getPaymentHistory(filter);
      setHistData(result);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to load payment history');
    } finally {
      setHistLoading(false);
    }
  }, [histPage, histStatus, histDateFrom, histDateTo]);

  const fetchSubscriptions = useCallback(async () => {
    setSubLoading(true);
    try {
      const filter = { page: subPage, pageSize: 25 };
      if (subStatus) filter.status = subStatus;
      const result = await adminService.getSubscriptions(filter);
      setSubData(result);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to load subscriptions');
    } finally {
      setSubLoading(false);
    }
  }, [subPage, subStatus]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);
  useEffect(() => { fetchSubscriptions(); }, [fetchSubscriptions]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Payments & Subscriptions</h1>
            <p className="text-muted-foreground mt-1">View all payment transactions and active subscriptions</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit">
          {['history', 'subscriptions'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors capitalize ${
                activeTab === tab ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab === 'history' ? 'Payment History' : 'Subscriptions'}
            </button>
          ))}
        </div>

        {/* Payment History Tab */}
        {activeTab === 'history' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Payment History</CardTitle>
                  <CardDescription>{histData.total} transactions</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchHistory}>
                  <RefreshCw className="h-4 w-4 mr-2" />Refresh
                </Button>
              </div>
              {/* Filters */}
              <div className="flex flex-wrap gap-3 pt-2">
                <Select value={histStatus} onValueChange={v => { setHistStatus(v === 'all' ? '' : v); setHistPage(1); }}>
                  <SelectTrigger className="w-36"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {PAYMENT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">From</Label>
                  <Input type="date" className="w-36" value={histDateFrom} onChange={e => { setHistDateFrom(e.target.value); setHistPage(1); }} />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">To</Label>
                  <Input type="date" className="w-36" value={histDateTo} onChange={e => { setHistDateTo(e.target.value); setHistPage(1); }} />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <PaginatedTable
                columns={['Organization', 'User', 'Plan', 'Amount', 'Status', 'Date']}
                rows={(histData.items || []).map(p => [
                  p.organizationName,
                  p.userName,
                  p.planName,
                  <span key="amt" className="font-medium">${p.amount?.toFixed(2)} {p.currency}</span>,
                  statusBadge(p.paymentStatus),
                  new Date(p.paymentDate).toLocaleDateString(),
                ])}
                data={histData}
                page={histPage}
                setPage={setHistPage}
                isLoading={histLoading}
              />
            </CardContent>
          </Card>
        )}

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Subscriptions</CardTitle>
                  <CardDescription>{subData.total} subscriptions</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={fetchSubscriptions}>
                  <RefreshCw className="h-4 w-4 mr-2" />Refresh
                </Button>
              </div>
              <div className="flex gap-3 pt-2">
                <Select value={subStatus} onValueChange={v => { setSubStatus(v === 'all' ? '' : v); setSubPage(1); }}>
                  <SelectTrigger className="w-36"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Paid">Active</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <PaginatedTable
                columns={['Organization', 'Plan', 'Price', 'Start', 'End', 'Status']}
                rows={(subData.items || []).map(s => [
                  s.organizationName,
                  s.planName,
                  <span key="price">${s.price?.toFixed(2)}</span>,
                  new Date(s.startDate).toLocaleDateString(),
                  new Date(s.endDate).toLocaleDateString(),
                  statusBadge(s.paymentStatus),
                ])}
                data={subData}
                page={subPage}
                setPage={setSubPage}
                isLoading={subLoading}
              />
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
